/**
 * 習慣関連ページの共通レイアウトコンポーネント
 *
 * 全ての習慣関連ページ（ダッシュボード、一覧、週間、月間）で使用する
 * 共通のレイアウト構造を提供する。
 *
 * 構成:
 * - SideNav: デスクトップ用サイドバーナビゲーション
 * - Header: ページタイトルとアクションボタン
 * - Content: ページ固有のコンテンツ（children）
 * - BottomNav: モバイル用ボトムナビゲーション
 *
 * Rails で言えば:
 * - app/views/layouts/application.html.erb に相当
 * - yield でコンテンツを差し込む構造
 *
 * @example
 * <HabitLayout
 *   title="今日の習慣"
 *   action={<Button>新規追加</Button>}
 * >
 *   <HabitCard ... />
 * </HabitLayout>
 */

import { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Settings } from 'lucide-react'
import { SideNav } from './SideNav'
import { BottomNav } from './BottomNav'
import * as Routes from '@/lib/routes'

interface HabitLayoutProps {
  /** ページのメインコンテンツ */
  children: ReactNode
  /** ヘッダーに表示するページタイトル */
  title: string
  /** ヘッダー右側に表示するアクション要素（ボタンなど） */
  action?: ReactNode
}

export function HabitLayout({ children, title, action }: HabitLayoutProps) {
  return (
    <div className="bg-background flex min-h-screen overflow-x-hidden">
      <SideNav />

      <main className="min-w-0 flex-1 pb-20 sm:pb-0">
        {/* Header */}
        <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 border-border sticky top-0 z-40 border-b backdrop-blur">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6">
            <h1 className="text-foreground text-xl font-semibold">{title}</h1>
            <div className="flex items-center gap-2">
              {action}
              {/* スマホのみ設定アイコンを表示 */}
              <Link
                href={Routes.settings_path()}
                className="text-muted-foreground hover:text-foreground -mr-2 p-2 sm:hidden"
              >
                <Settings className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 sm:p-6">{children}</div>
      </main>

      <BottomNav />
    </div>
  )
}
